import { Component, Input, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { firstValueFrom } from "rxjs";
import { ModalController, IonicModule } from "@ionic/angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Language } from "../../entities/Language";
import { AuthUser } from "../../entities/AuthUser";
import { AccountsService } from "../../services/accounts/accounts.service";
import { AuthService } from "../../services/auth/auth.service";

@Component({
    standalone: true,
    imports: [CommonModule, IonicModule, TranslateModule],
    selector: "app-language-selection-modal",
    templateUrl: "./language-selection-modal.component.html",
    styleUrls: ["./language-selection-modal.component.scss"]
})
export class LanguageSelectionModalComponent implements OnInit {
    @Input() authUser: AuthUser;

    availableLanguages: Language[] = [];
    defaultLanguage: Language | null = null;
    selectedLanguage: Language | null = null;
    isLoading = false;

    constructor(
        private modalController: ModalController,
        private translate: TranslateService,
        private accountsService: AccountsService,
        private authService: AuthService
    ) {}

    ngOnInit() {
        // authUser is guaranteed to exist and have languages due to validation in app.component.ts
        this.availableLanguages = this.authUser.available_languages;
        this.defaultLanguage = this.authUser.getDivisionDefaultLanguage();

        // Pre-select the default language if available
        if (this.defaultLanguage) {
            this.selectedLanguage = this.defaultLanguage;
        }
    }

    selectLanguage(language: Language) {
        this.selectedLanguage = language;
    }

    onLanguageChange(event: any) {
        const languageId = event.detail.value;
        const language = this.availableLanguages.find(
            (lang) => lang.id === languageId
        );
        if (language) {
            this.selectedLanguage = language;
        }
    }

    async confirmSelection() {
        if (!this.selectedLanguage) {
            return;
        }

        this.isLoading = true;

        try {
            // Update user's language via API
            await firstValueFrom(
                this.accountsService.patch({
                    language_id: this.selectedLanguage.id
                })
            );

            // Wait for the refreshed profile to land in the auth state. Not
            // waiting leaves the stored profile on its old language, so the
            // modal is shown again on the next run and the user is stuck.
            await firstValueFrom(this.authService.refreshUserProfile());

            // Update i18n to the selected language after profile is refreshed
            await firstValueFrom(
                this.translate.use(this.selectedLanguage.code)
            );

            // Close modal with success flag. App component picks this up and
            // re-emits the auth state, which continues the interrupted boot.
            await this.modalController.dismiss({ languageUpdated: true });
        } catch (error) {
            console.error("Error updating language:", error);
            // TODO: Toon gebruiksvriendelijke foutmelding
            // Bijv: this.alertService.error('Kon taal niet bijwerken. Probeer opnieuw.');
        } finally {
            this.isLoading = false;
        }
    }

    isDefaultLanguage(language: Language): boolean {
        return this.defaultLanguage?.id === language.id;
    }

    isSelected(language: Language): boolean {
        return this.selectedLanguage?.id === language.id;
    }
}
